import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getQueryParams } from 'utils/common.util';

type UseActionSheetType = {
  isOpenActionSheet: string;
  isOnDismissCalledStatus: boolean;
  handleDefault: () => void;
  handleApply: () => void;
  handleDismiss: () => void;
};

export const useActionSheet = (
  actionSheet: string,
  open: boolean,
  onDismiss: () => void,
  onApply?: () => void,
  onDefault?: () => void,
): UseActionSheetType => {
  let timeout: NodeJS.Timeout = null;
  const router = useRouter();
  const [isOnDismissCalledStatus, setIsOnDismissCalledStatus] = useState(false);
  const isOpenActionSheet = getQueryParams(router.asPath)?.actionSheet;

  useEffect(() => {
    const operator = router.asPath.includes('?') ? '&' : '?';

    open &&
      !isOpenActionSheet &&
      router.push(
        `${router.asPath}${operator}actionSheet=${actionSheet}`,
        undefined,
        {
          shallow: true,
        },
      );
    router.beforePopState(() => {
      onDismiss();
      return true;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [open]);

  // after run onDismiss function the isOnDismissCalledStatus state should be false to handle next reopen of action sheet
  useEffect(() => {
    !isOpenActionSheet && setIsOnDismissCalledStatus(false);
  }, [isOpenActionSheet]);

  const handleDefault = (): void => {
    onDefault?.();
    router.back();
  };

  const handleApply = (): void => {
    router.back();
    onApply?.();
  };

  const handleDismiss = (): void => {
    // the onDismiss function runs two times when user pulls down the action sheet
    // therefor we should handle it with an if statement
    // Add setTimeout to prevent click buttons behind backdrop in mobile
    // remember to always clearTimeout to prevent bug
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      if (!isOnDismissCalledStatus) {
        router.back();
        setIsOnDismissCalledStatus(true);
      }
    }, 100);
  };

  return {
    isOpenActionSheet,
    isOnDismissCalledStatus,
    handleDefault,
    handleApply,
    handleDismiss,
  };
};
